import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatRupeesVisual, formatDate } from "@/lib/format";
import FYPicker, { currentFYStart } from "@/components/FYPicker";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Download, TrendingUp, TrendingDown, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  land: "Land", loan: "Loan Given", loan_taken: "Loan Taken",
  company_share: "Company Share", business_franchise: "Franchise",
  sebi_startup_fund: "AIF / Startup Fund", sip: "SIP", lumpsum: "Lumpsum / Stocks",
};

const METHOD_LABEL = {
  manual: "Manual", cagr: "CAGR", loan_pv: "Loan PV",
  book_value: "Book × P/B", pe_multiple: "PE × Earnings",
  last_round: "Last round", ebitda_multiple: "EBITDA × mult",
  nav: "NAV × units", stored: "Stored",
};

export default function MarketValuationReport() {
  const [fy, setFy] = useState("all");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [asOf, setAsOf] = useState(null);
  const [sortBy, setSortBy] = useState("mv_desc");

  const load = async () => {
    setBusy(true);
    try {
      const params = fy === "all" ? "" : `?fy=${fy}`;
      const r = await api.get(`/portfolio/mtm${params}`);
      setData(r.data);
      setAsOf(r.data.as_of_date);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fy]);

  const rows = useMemo(() => {
    if (!data) return [];
    const arr = [...data.items];
    switch (sortBy) {
      case "mv_desc": return arr.sort((a, b) => b.market_value - a.market_value);
      case "gain_desc": return arr.sort((a, b) => b.gain - a.gain);
      case "gain_pct_desc": return arr.sort((a, b) => b.gain_percent - a.gain_percent);
      case "type": return arr.sort((a, b) => (a.type || "").localeCompare(b.type || ""));
      default: return arr;
    }
  }, [data, sortBy]);

  const s = data?.summary;

  const exportCsv = () => {
    if (!data) return;
    const header = ["Type", "Project code", "Name", "Invested on", "Cost", "Market value", "Gain", "Gain %", "Method"];
    const lines = [header.join(",")];
    rows.forEach((r) => {
      lines.push([
        TYPE_LABEL[r.type] || r.type,
        r.code || "",
        `"${(r.name || "").replace(/"/g, '""')}"`,
        r.invested_at || "",
        r.cost, r.market_value, r.gain, r.gain_percent,
        METHOD_LABEL[r.method] || r.method,
      ].join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `market-valuation_${asOf || "today"}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Insights · Portfolio</div>
          <h1 className="mt-2 font-editorial font-light text-[38px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="mtm-title">
            Market <em className="italic">valuation.</em>
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-lg">
            Mark-to-market for every investment on {asOf ? formatDate(asOf) : "today"}. Auto-computed using CFO-grade formulas per asset class.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <FYPicker value={fy} onChange={setFy} size="sm" allowAll />
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-40 h-9 text-[12px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="mv_desc">Sort · MV ↓</SelectItem>
              <SelectItem value="gain_desc">Sort · Gain ↓</SelectItem>
              <SelectItem value="gain_pct_desc">Sort · Gain % ↓</SelectItem>
              <SelectItem value="type">Sort · Type</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={load} variant="outline" className="rounded-md h-9 w-9 p-0" title="Refresh"><RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /></Button>
          <Link to="/market-valuation/bulk-nav">
            <Button variant="outline" className="rounded-md gap-1.5" data-testid="mtm-bulk-nav">
              <Sparkles className="h-4 w-4 text-red-600" /> Bulk NAV update
            </Button>
          </Link>
          <Button onClick={exportCsv} variant="outline" className="rounded-md" data-testid="mtm-export"><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
        </div>
      </div>

      {/* KPI cards */}
      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <KPI label="Total cost" value={formatRupeesVisual(s.total_cost)} testId="k-cost" />
          <KPI label="Market value" value={formatRupeesVisual(s.total_market_value)} tone="text-slate-900" testId="k-mv" />
          <KPI label="Unrealised gain"
            value={`${s.unrealised_gain >= 0 ? "+" : ""}${formatRupeesVisual(s.unrealised_gain)}`}
            tone={s.unrealised_gain >= 0 ? "text-emerald-700" : "text-red-600"}
            icon={s.unrealised_gain >= 0 ? TrendingUp : TrendingDown}
            testId="k-gain" />
          <KPI label="Gain %"
            value={`${s.gain_percent >= 0 ? "+" : ""}${s.gain_percent.toFixed(2)}%`}
            tone={s.gain_percent >= 0 ? "text-emerald-700" : "text-red-600"}
            testId="k-pct" />
          <KPI label="XIRR (annualised)"
            value={s.xirr_percent === null || s.xirr_percent === undefined ? "—" : `${s.xirr_percent.toFixed(2)}%`}
            tone="text-slate-900" testId="k-xirr" />
        </div>
      )}

      {/* Rows */}
      <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Investment</th>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Type</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Cost</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Market value</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Gain</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Gain %</th>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Method</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-slate-500">No investments to value.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`row-${r.id}`}>
                  <td className="p-3">
                    <Link to={`/investments/${r.id}`} className="text-slate-900 hover:text-red-600 font-medium">{r.name}</Link>
                    {r.code && <span className="ml-2 text-[10px] font-mono-fin text-slate-400">{r.code}</span>}
                  </td>
                  <td className="p-3 text-slate-600">{TYPE_LABEL[r.type] || r.type}</td>
                  <td className="p-3 text-right font-mono-fin">{formatRupeesVisual(r.cost)}</td>
                  <td className="p-3 text-right font-mono-fin text-slate-900 font-semibold">{formatRupeesVisual(r.market_value)}</td>
                  <td className={`p-3 text-right font-mono-fin ${r.gain >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                    {r.gain >= 0 ? "+" : ""}{formatRupeesVisual(r.gain)}
                  </td>
                  <td className={`p-3 text-right font-mono-fin ${r.gain_percent >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                    {r.gain_percent >= 0 ? "+" : ""}{r.gain_percent.toFixed(2)}%
                  </td>
                  <td className="p-3">
                    <span className="inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-slate-200 bg-slate-50 text-slate-600 rounded-sm">
                      {METHOD_LABEL[r.method] || r.method}
                    </span>
                    <span className="ml-1.5 text-[10px] text-slate-400">{r.source}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <ul className="md:hidden divide-y divide-slate-100">
          {rows.length === 0 && (
            <li className="p-8 text-center text-slate-500 text-[13px]">No investments to value.</li>
          )}
          {rows.map((r) => (
            <li key={r.id} className="p-4" data-testid={`mrow-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <Link to={`/investments/${r.id}`}
                    className="block text-[13px] font-semibold text-slate-900 hover:text-red-600 truncate">
                    {r.name}
                  </Link>
                  <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="text-[10.5px] text-slate-500">{TYPE_LABEL[r.type] || r.type}</span>
                    {r.code && <span className="text-[10px] font-mono-fin text-slate-400">· {r.code}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono-fin text-[15px] font-semibold text-slate-900 tabular-nums">{formatRupeesVisual(r.market_value)}</div>
                  <div className={`text-[11px] font-mono-fin tabular-nums ${r.gain >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                    {r.gain >= 0 ? "+" : ""}{r.gain_percent.toFixed(2)}%
                  </div>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px] gap-2">
                <div className="text-slate-500">
                  Cost <span className="font-mono-fin text-slate-700 tabular-nums">{formatRupeesVisual(r.cost)}</span>
                </div>
                <div className={`font-mono-fin tabular-nums ${r.gain >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                  {r.gain >= 0 ? "+" : ""}{formatRupeesVisual(r.gain)}
                </div>
                <span className="text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 border border-slate-200 bg-slate-50 text-slate-600 rounded-sm">
                  {METHOD_LABEL[r.method] || r.method}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <p className="text-[11px] text-slate-500">
        Methods used: <span className="font-mono-fin">manual</span> = snapshot from you; <span className="font-mono-fin">auto</span> = live-computed by formula; <span className="font-mono-fin">stored</span> = fallback to the stored current-value field.
      </p>
    </div>
  );
}

function KPI({ label, value, tone, sub, icon: Icon, testId }) {
  return (
    <div className="p-4 border border-slate-200 bg-white rounded-md">
      <div className="text-[10px] tracking-wider uppercase text-slate-500 flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className={`mt-1.5 font-mono-fin text-xl font-semibold ${tone || "text-slate-900"}`} data-testid={testId}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
